import React, { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Send } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { api, wsUrl } from '@/lib/api';
import { toast } from 'sonner';

export default function ChatTab({ event, user }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const wsRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get(`/events/${event.id}/chat`);
        if (mounted) setMessages(r.data || []);
      } catch (err) {
        console.error('Failed to load chat history:', err);
      }
    })();
    const url = wsUrl(`/ws/events/${event.id}`);
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data?.event === 'chat_message') {
          setMessages((arr) => [...arr, data.data]);
        }
      } catch (err) {
        console.error('Bad WS message:', err);
      }
    };
    ws.onerror = (err) => { console.error('Chat WS error:', err); };
    return () => {
      mounted = false;
      try { ws.close(); } catch (err) { console.error('WS close error:', err); }
    };
  }, [event.id]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!text.trim()) return;
    const t = text.trim();
    setText('');
    // Try websocket first; fallback to REST
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: 'chat', text: t }));
    } else {
      try { const r = await api.post(`/events/${event.id}/chat`, { text: t }); setMessages((arr) => [...arr, r.data]); } catch (err) { toast.error('Failed'); }
    }
  };

  return (
    <div className="flex flex-col h-[60vh]" data-testid="chat-tab">
      <Card className="rounded-2xl flex-1 flex flex-col overflow-hidden">
        <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="chat-messages">
          {messages.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Say hi to your group.</p>}
          {messages.map((m) => {
            const mine = m.user?.id === user?.id || m.user_id === user?.id;
            return (
              <div key={m.id} className={`flex items-end gap-2 ${mine ? 'flex-row-reverse' : ''}`} data-testid={`chat-msg-${m.id}`}>
                <Avatar className="h-7 w-7"><AvatarImage src={m.user?.picture} /><AvatarFallback>{(m.user?.name || 'U').slice(0,1)}</AvatarFallback></Avatar>
                <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${mine ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]' : 'bg-secondary'}`}>
                  {!mine && <div className="text-[11px] font-semibold mb-0.5 opacity-80">{m.user?.name}</div>}
                  <div className="whitespace-pre-line">{m.text}</div>
                  <div className={`text-[10px] mt-1 opacity-70 ${mine ? 'text-right' : ''}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
              </div>
            );
          })}
        </div>
        <form onSubmit={send} className="border-t p-2 flex items-center gap-2 bg-card" data-testid="chat-composer">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" data-testid="chat-input" className="rounded-xl" />
          <Button type="submit" data-testid="chat-send" className="rounded-xl gradient-fab text-white"><Send className="h-4 w-4" /></Button>
        </form>
      </Card>
    </div>
  );
}
